import React, { useState, useEffect, useRef } from "react";
import { t } from "./i18n";
import {
  resendVerificationCode,
  verifyOtp,
  resendOtp,
} from "./api/apiFunctions";

export default function VerificationModal({
  open,
  onClose,
  onVerify,
  email,
  lang = "en",
  token,
  showResend = false,
}) {
  const [verificationCode, setVerificationCode] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState(120);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (open) {
      setVerificationCode(["", "", "", "", "", ""]);
      setError("");
      setTimeLeft(120);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (timeLeft === 0) return;

    const timer = setTimeout(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft, open]);

  const handleDigitChange = (index, value) => {
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    setError("");

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const pastedData = e.clipboardData.getData("text");

    if (!pastedData) return;

    const cleaned = pastedData.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6);

    if (!cleaned) return;

    const newCode = ["", "", "", "", "", ""];

    for (let i = 0; i < cleaned.length; i++) {
      newCode[i] = cleaned[i];
    }

    setVerificationCode(newCode);
    setError("");

    const nextIndex = cleaned.length < 6 ? cleaned.length : 5;
    inputRefs.current[nextIndex]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!verificationCode[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newCode = [...verificationCode];
        newCode[index] = "";
        setVerificationCode(newCode);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token =
      localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    const codeString = verificationCode.join("");
    if (!codeString || codeString.length !== 6) {
      setError(
        t("verificationCodeRequired", lang) ||
          "Please enter a valid 6-digit verification code",
      );
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      const response = await verifyOtp(codeString, token);
      if (response.success) {
        onVerify(codeString);
      } else {
        setError(
          response.message ||
            t("verificationFailed", lang) ||
            "Verification failed. Please try again.",
        );
      }
    } catch (error) {
      console.error("Verification failed:", error);
      setError(
        error.message ||
          t("verificationFailed", lang) ||
          "Verification failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setTimeLeft(120);
      setIsResendingOtp(true);
      setError("");
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");
      const response = await resendOtp(token);
      if (!response.success) {
        setError(
          response.message || "Failed to resend code. Please try again.",
        );
      }
    } catch (error) {
      console.error("Failed to resend code:", error);
      setError("Failed to resend code. Please try again.");
    } finally {
      setIsResendingOtp(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-3xl shadow-2xl p-0 w-11/12 sm:w-full sm:max-w-md border border-[#e5e7eb] relative overflow-hidden">
        <button
          className="absolute top-5 right-6 text-gray-400 hover:text-gray-600 text-2xl font-bold z-10"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>

        <div className="flex flex-col items-center px-8 pt-10 pb-8">
          {/* Email Icon */}
          <div className="bg-[#f7f8fa] rounded-full p-4 mb-4 flex items-center justify-center">
            <svg width="44" height="44" fill="none" viewBox="0 0 24 24">
              <path
                stroke="#2d348f"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-extrabold text-[#2d348f] mb-2 text-center">
            {t("verifyEmail", lang) || "Verify Your Email"}
          </h2>

          {/* Description */}
          <p className="text-[#837f90] mb-6 text-center text-base">
            {t("verificationDescription", lang) ||
              "We've sent a 6-digit verification code to"}
          </p>

          {/* Email Display */}
          <div className="bg-[#f7f8fa] rounded-lg px-4 py-2 mb-6">
            <span className="text-[#2d348f] font-semibold">{email}</span>
          </div>

          {/* Verification Form */}
          <form onSubmit={handleSubmit} className="w-full">
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#2d348f] mb-2 text-center">
                {t("enterVerificationCode", lang) || "Enter Verification Code"}
              </label>
              <div className="flex justify-center gap-3">
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    value={digit}
                    onChange={(e) => handleDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-12 text-center text-xl font-bold border-2 border-[#4962d8] rounded-lg focus:border-[#2d348f] focus:outline-none focus:ring-2 focus:ring-[#2d348f]/20"
                    maxLength={1}
                    autoComplete="one-time-code"
                    onPaste={handlePaste}
                  />
                ))}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-red-500 text-sm text-center mb-4">
                *{error}
              </div>
            )}

            {/* Timer and Resend */}
            <div className="text-center mb-6">
              {timeLeft > 0 ? (
                <p className="text-sm text-[#837f90]">
                  {t("resendIn", lang) || "Resend code in"}
                  <span className="font-semibold text-[#2d348f] ml-1">
                    {formatTime(timeLeft)}
                  </span>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isResendingOtp}
                  className="text-sm text-[#2d348f] font-semibold hover:underline"
                >
                  {t("resendCode", lang) || "Resend Code"}
                </button>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-[#2d348f] text-white font-semibold py-3 rounded-xl shadow hover:bg-[#4962d8] transition-all mb-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || verificationCode.join("").length !== 6}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t("verifying", lang) || "Verifying..."}
                </div>
              ) : (
                t("verifyEmail", lang) || "Verify Email"
              )}
            </button>

            {/* Cancel Button */}
            <button
              type="button"
              className="w-full bg-gray-100 text-[#2d348f] font-medium py-3 rounded-xl hover:bg-gray-200 transition-all"
              onClick={onClose}
            >
              {t("cancel", lang) || "Cancel"}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-xs text-[#837f90]">
              {t("verificationHelp", lang) ||
                "Didn't receive the code? Check your spam folder or try resending."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
